import { Button, Icon, Input, Badge, Tooltip } from '@aidkitorg/component-library';
import { SupportedLanguage, SupportedLanguages, LanguageNames, APPLICANT_FACING, BACKEND_ONLY } from '@aidkitorg/i18n/lib';
import * as React from 'react';
import { usePost } from '../API';
import { useDebouncedMemo } from '../Hooks/Debounce';

type SortOption = 'alphabetical' | 'last_modified' | 'needs_review' | 'applicant_facing_first';
type FilterOption = 'all' | 'needs_review' | 'human_reviewed';
type AudienceFilterOption = 'all' | typeof APPLICANT_FACING | typeof BACKEND_ONLY;
type TranslationEdits = Partial<Record<SupportedLanguage, Record<string, string>>>;
type TranslationCache = Partial<Record<SupportedLanguage, TranslationsResponse>>;

const DEFAULT_SORT: SortOption = 'needs_review';
const DEFAULT_FILTER: FilterOption = 'needs_review';
const STORAGE_KEY = 'translation-edits';

const CONFIRM_MESSAGES = {
  discardLanguage: (count: number, lang: string) =>
    `Discard all ${count} ${lang} changes?`,
  discardAll: (count: number) =>
    `Discard all ${count} unsaved changes?`,
};

interface Translation {
  lang: string;
  key: string;
  ai_translation: string;
  human_translation?: string;
  last_modified_by: string;
  last_modified: string;
  needs_review?: boolean;
  audience?: typeof APPLICANT_FACING | typeof BACKEND_ONLY;
}

interface TranslationStats {
  total: number;
  ai_only: number;
  human_reviewed: number;
  coverage_percent: number;
}

interface TranslationsResponse {
  translations: Translation[];
  lang: string;
  stats: TranslationStats;
}

const createTranslationMap = (translations?: Translation[]): Record<string, Translation> => {
  if (!translations) return {};
  return translations.reduce((map, t) => ({ ...map, [t.key]: t }), {});
};

interface TranslationControlsProps {
  baseLang: SupportedLanguage | '';
  setBaseLang: (lang: SupportedLanguage | '') => void;
  translationLang: SupportedLanguage;
  setTranslationLang: (lang: SupportedLanguage) => void;
  searchText: string;
  setSearchText: (text: string) => void;
  sortBy: SortOption;
  setSortBy: (sort: SortOption) => void;
  filterBy: FilterOption;
  setFilterBy: (filter: FilterOption) => void;
  audienceFilter: AudienceFilterOption;
  setTagFilter: (tag: AudienceFilterOption) => void;
  baseError: string | null;
  translationError: string | null;
  translationStats: TranslationStats | null;
}

const TranslationControls = React.memo<TranslationControlsProps>(({
  baseLang,
  setBaseLang,
  translationLang,
  setTranslationLang,
  searchText,
  setSearchText,
  sortBy,
  setSortBy,
  filterBy,
  setFilterBy,
  audienceFilter,
  setTagFilter,
  baseError,
  translationError,
  translationStats
}) => {
  return (
    <>
      {/* Language Selection */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Base Language (for comparison)
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            value={baseLang}
            onChange={(e) => setBaseLang(e.target.value as SupportedLanguage | '')}
          >
            <option value="">None</option>
            {SupportedLanguages.filter(lang => lang !== translationLang).map(lang => (
              <option key={lang} value={lang}>
                {LanguageNames[lang]}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Translation Language
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            value={translationLang}
            onChange={(e) => setTranslationLang(e.target.value as SupportedLanguage)}
          >
            {SupportedLanguages.map(lang => (
              <option key={lang} value={lang}>
                {LanguageNames[lang]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error Banners */}
      {baseError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4 text-red-800">
          {baseError} (base language). Please try again.
        </div>
      )}
      {translationError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4 text-red-800">
          {translationError}. Please try again.
        </div>
      )}

      {translationStats && (
        <div className="flex gap-4 mb-4">
          <Badge variant="info">
            Total: {translationStats.total}
          </Badge>
          <Badge variant="warning">
            AI Only: {translationStats.ai_only}
          </Badge>
          <Badge variant="success">
            Human Reviewed: {translationStats.human_reviewed} ({translationStats.coverage_percent}%)
          </Badge>
        </div>
      )}

      {/* Search, Filter, and Sort */}
      <div className="flex items-end gap-4 mb-4">
        <div className="flex-1">
          <Input
            label="Search"
            placeholder="Search by key or text..."
            value={searchText}
            onChange={(e) => setSearchText(e.currentTarget.value)}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
            Status
          </label>
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
            {([['all', 'All'], ['needs_review', 'Needs Review'], ['human_reviewed', 'Reviewed']] as const).map(([value, label], i) => (
              <button
                key={value}
                onClick={() => setFilterBy(value)}
                className={[
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  i > 0 ? 'border-l border-gray-300' : '',
                  filterBy === value
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50',
                ].join(' ')}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
            Audience
          </label>
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
            {([['all', 'All'], [APPLICANT_FACING, 'Applicant Facing'], [BACKEND_ONLY, 'Backend Only']] as const).map(([value, label], i) => (
              <button
                key={value}
                onClick={() => setTagFilter(value)}
                className={[
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  i > 0 ? 'border-l border-gray-300' : '',
                  audienceFilter === value
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50',
                ].join(' ')}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
            Sort By
          </label>
          <select
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
          >
            <option value="alphabetical">Alphabetical</option>
            <option value="last_modified">Last Modified</option>
            <option value="needs_review">Needs Review</option>
            <option value="applicant_facing_first">Applicant Facing First</option>
          </select>
        </div>
      </div>
    </>
  );
});

interface TranslationsTableProps {
  baseLang: SupportedLanguage | '';
  translationLang: SupportedLanguage;
  filteredKeys: string[];
  baseTranslationsMap: Record<string, Translation>;
  translationMap: Record<string, Translation>;
  editingTranslations: TranslationEdits;
  translationError: string | null;
  onRowClick: (key: string, trans: Translation | undefined) => void;
  onApproveAI: (key: string, aiTranslation: string) => void;
}

const TranslationsTable = React.memo<TranslationsTableProps>(({
  baseLang,
  translationLang,
  filteredKeys,
  baseTranslationsMap,
  translationMap,
  editingTranslations,
  translationError,
  onRowClick,
  onApproveAI
}) => {
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
              Key
            </th>
            {baseLang && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                {LanguageNames[baseLang]}
              </th>
            )}
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
              {LanguageNames[translationLang]} (AI)
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
              {LanguageNames[translationLang]} (Human)
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
              Status
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
              Audience
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredKeys.map((key) => {
            const base = baseTranslationsMap[key];
            const trans = translationMap[key];
            const localEdit = editingTranslations[translationLang]?.[key];
            const displayValue = localEdit ?? trans?.human_translation;

            return (
              <tr
                key={key}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onRowClick(key, trans)}
              >
                <td className="px-4 py-3 text-xs text-gray-900 font-mono max-w-0">
                  <Tooltip text={key} side="top" asChild>
                    <span className="truncate block">{key}</span>
                  </Tooltip>
                </td>
                {baseLang && (
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <Tooltip text={base?.human_translation ?? base?.ai_translation ?? '-'} side="top" asChild>
                        <span className="truncate">{base?.human_translation ?? base?.ai_translation ?? '-'}</span>
                      </Tooltip>
                      {base?.human_translation ? (
                        <Icon name="CheckCircleIcon" size="sm" className="text-green-600 flex-shrink-0" role="informative" title="Human reviewed" />
                      ) : base?.ai_translation ? (
                        <Icon name="SparklesIcon" size="sm" className="text-blue-500 flex-shrink-0" role="informative" title="AI translation" />
                      ) : null}
                    </div>
                  </td>
                )}
                <td className="px-4 py-3 text-sm text-gray-700 max-w-0">
                  <Tooltip text={trans?.ai_translation ?? '-'} side="top" asChild>
                    <span className="truncate block">{trans?.ai_translation ?? '-'}</span>
                  </Tooltip>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 max-w-0">
                  <Tooltip text={displayValue ?? '-'} side="top" asChild>
                    <span className="truncate block">{displayValue ?? <span className="text-gray-400">-</span>}</span>
                  </Tooltip>
                </td>
                <td className="px-4 py-3">
                  {localEdit ? (
                    <Badge variant="info">Edited</Badge>
                  ) : trans?.needs_review ? (
                    <Badge variant="warning">Needs Review</Badge>
                  ) : trans?.human_translation ? (
                    <Badge variant="success">Reviewed</Badge>
                  ) : trans?.ai_translation ? (
                    <Badge variant="warning">AI Only</Badge>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {trans?.audience === APPLICANT_FACING ? (
                    <Badge variant="info">Applicant</Badge>
                  ) : trans?.audience === BACKEND_ONLY ? (
                    <Badge variant="neutral">Backend</Badge>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  {trans?.ai_translation && !trans?.human_translation && !localEdit && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onApproveAI(key, trans.ai_translation)}
                    >
                      Approve AI
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {filteredKeys.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          {translationError ? 'Unable to load translations.' : 'No translations found.'}
        </div>
      )}
    </div>
  );
});

interface EditTranslationModalProps {
  viewingKey: string | null;
  baseLang: SupportedLanguage | '';
  translationLang: SupportedLanguage;
  baseTranslationsMap: Record<string, Translation>;
  translationMap: Record<string, Translation>;
  updatedHumanTranslationValue: string;
  onClose: () => void;
  onUpdateValue: (value: string) => void;
  onSave: () => void;
}

const EditTranslationModal = React.memo<EditTranslationModalProps>(({
  viewingKey,
  baseLang,
  translationLang,
  baseTranslationsMap,
  translationMap,
  updatedHumanTranslationValue,
  onClose,
  onUpdateValue,
  onSave
}) => {
  if (!viewingKey) return null;

  const baseTranslation = baseTranslationsMap[viewingKey];
  const translation = translationMap[viewingKey];
  const isBaseHumanReviewed = baseTranslation?.human_translation !== undefined;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-bold">Translation Details</h2>
              <p className="text-sm text-gray-600 font-mono mt-1">{viewingKey}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <Icon name="XMarkIcon" size="md" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Show base language translation if selected */}
            {baseLang && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  {LanguageNames[baseLang]} Translation
                  {baseTranslation && (
                    <span className="text-xs font-normal">
                      {isBaseHumanReviewed ? (
                        <Badge variant="success">Human Reviewed</Badge>
                      ) : (
                        <Badge variant="info">AI Only</Badge>
                      )}
                    </span>
                  )}
                </label>
                <div className={`bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm ${isBaseHumanReviewed ? 'text-gray-700' : 'text-gray-500 italic'}`}>
                  {baseTranslation?.human_translation ?? baseTranslation?.ai_translation ?? '-'}
                </div>
              </div>
            )}

            {/* AI Translation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {LanguageNames[translationLang]} (AI Translation)
              </label>
              <div className="bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700">
                {translation?.ai_translation ?? '-'}
              </div>
            </div>

            {/* Human Translation (Read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current {LanguageNames[translationLang]} (Human Translation)
              </label>
              <div className="bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700">
                {translation?.human_translation || <span className="text-gray-400 italic">Not set</span>}
              </div>
            </div>

            {/* Updated Human Translation (Editable) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Updated {LanguageNames[translationLang]} (Human Translation)
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                value={updatedHumanTranslationValue}
                onChange={(e) => onUpdateValue(e.currentTarget.value)}
                placeholder="Enter human-reviewed translation..."
              />
            </div>

            {/* Metadata */}
            {translation && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <div>
                      {translation.needs_review ? (
                        <Badge variant="warning">Needs Review</Badge>
                      ) : (
                        <Badge variant="success">Reviewed</Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Modified By
                    </label>
                    <div className="text-sm text-gray-700">
                      {translation.last_modified_by}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Modified
                  </label>
                  <div className="text-sm text-gray-700">
                    {new Date(translation.last_modified).toLocaleString()}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onSave}
            >
              Save (Local)
            </Button>
          </div>

          <div className="mt-2 text-xs text-gray-500 text-right">
            Changes are saved locally. Use "Save All" to push to database.
          </div>
        </div>
      </div>
    </div>
  );
});

interface SaveAllModalProps {
  editingTranslations: TranslationEdits;
  allTranslationsData: TranslationCache;
  onClose: () => void;
  onSaveLanguage: (lang: SupportedLanguage) => Promise<void>;
  onSaveAll: () => Promise<void>;
  onDiscardLanguage: (lang: SupportedLanguage) => void;
  onDiscardAll: () => void;
  isSaving: boolean;
}

const SaveAllModal = React.memo<SaveAllModalProps>(({
  editingTranslations,
  allTranslationsData,
  onClose,
  onSaveLanguage,
  onSaveAll,
  onDiscardLanguage,
  onDiscardAll,
  isSaving
}) => {
  const languagesWithEdits = Object.entries(editingTranslations)
    .filter(([_, edits]) => Object.keys(edits).length > 0)
    .map(([lang, edits]) => ({ lang: lang as SupportedLanguage, edits }));

  const totalEdits = languagesWithEdits.reduce((sum, { edits }) => sum + Object.keys(edits).length, 0);

  if (languagesWithEdits.length === 0) {
    return null;
  }

  const getCurrentValue = (lang: SupportedLanguage, key: string): string | undefined => {
    const langData = allTranslationsData[lang];
    if (!langData) return undefined;
    const translation = langData.translations.find(t => t.key === key);
    return translation?.human_translation;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-bold">Save Translations</h2>
              <p className="text-sm text-gray-600 mt-1">
                {totalEdits} unsaved translation{totalEdits !== 1 ? 's' : ''} across {languagesWithEdits.length} language{languagesWithEdits.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isSaving}
            >
              <Icon name="XMarkIcon" size="md" />
            </button>
          </div>

          <div className="space-y-6">
            {languagesWithEdits.map(({ lang, edits }) => {
              const editCount = Object.keys(edits).length;
              return (
                <div key={lang} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{LanguageNames[lang]}</h3>
                      <p className="text-sm text-gray-600">{editCount} edited translation{editCount !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          if (confirm(CONFIRM_MESSAGES.discardLanguage(editCount, LanguageNames[lang]))) {
                            onDiscardLanguage(lang);
                          }
                        }}
                        disabled={isSaving}
                      >
                        Discard
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onSaveLanguage(lang)}
                        disabled={isSaving}
                      >
                        Save {LanguageNames[lang]}
                      </Button>
                    </div>
                  </div>

                  <div className="max-h-60 overflow-y-auto bg-gray-50 rounded p-3 space-y-3">
                    {Object.entries(edits).map(([key, newValue]) => {
                      const currentValue = getCurrentValue(lang, key);
                      return (
                        <div key={key} className="border-b border-gray-200 pb-3 last:border-b-0">
                          <div className="text-xs font-mono text-gray-600 mb-2">{key}</div>
                          <div className="space-y-1">
                            <div className="flex items-start gap-2">
                              <span className="text-xs font-medium text-gray-500 flex-shrink-0">Current:</span>
                              <span className="text-xs text-gray-700 italic">
                                {currentValue || <span className="text-gray-400">Not set</span>}
                              </span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-xs font-medium text-green-600 flex-shrink-0">Updated:</span>
                              <span className="text-xs text-gray-900 font-medium">{newValue}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-between items-center mt-6 pt-4 border-t">
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  if (confirm(CONFIRM_MESSAGES.discardAll(totalEdits))) {
                    onDiscardAll();
                    onClose();
                  }
                }}
                disabled={isSaving}
                className="text-red-600 hover:text-red-700"
              >
                Discard All
              </Button>
            </div>
            <Button
              variant="primary"
              onClick={onSaveAll}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : `Save All (${totalEdits})`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

export const TranslationOverview = () => {
  const getTranslations = usePost('/api/translations/get');
  const updateHumanTranslation = usePost('/api/translations/update');

  const [baseLang, setBaseLang] = React.useState<SupportedLanguage | ''>('en');
  const [translationLang, setTranslationLang] = React.useState<SupportedLanguage>('es');
  const [searchText, setSearchText] = React.useState('');
  const debouncedSearchText = useDebouncedMemo(() => searchText, 300, [searchText]);
  const [sortBy, setSortBy] = React.useState<SortOption>(DEFAULT_SORT);
  const [filterBy, setFilterBy] = React.useState<FilterOption>(DEFAULT_FILTER);
  const [audienceFilter, setTagFilter] = React.useState<AudienceFilterOption>('all');
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 50;

  const [loadedTranslations, setLoadedTranslations] = React.useState<TranslationCache>({});
  const [languageErrors, setLanguageErrors] = React.useState<Partial<Record<SupportedLanguage, string>>>({});

  const [viewingKey, setViewingKey] = React.useState<string | null>(null);
  const [updatedHumanTranslationValue, setUpdatedHumanTranslationValue] = React.useState('');

  const [editingTranslations, setEditingTranslations] = React.useState<TranslationEdits>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch (err) {
      console.error('Failed to load saved edits:', err);
      return {};
    }
  });
  const editingTranslationsRef = React.useRef(editingTranslations);
  const [showSaveModal, setShowSaveModal] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    editingTranslationsRef.current = editingTranslations;
    try {
      if (Object.keys(editingTranslations).length > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(editingTranslations));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      console.error('Failed to save edits to localStorage:', err);
    }
  }, [editingTranslations]);

  // Load translations for a language
  const loadLanguage = React.useCallback(async (lang: SupportedLanguage) => {
    // Skip if already loaded
    if (loadedTranslations[lang]) return;

    setLanguageErrors(prev => {
      const updated = { ...prev };
      delete updated[lang];
      return updated;
    });

    try {
      const response = await getTranslations({ lang });
      setLoadedTranslations(prev => ({
        ...prev,
        [lang]: response as TranslationsResponse
      }));
    } catch (err) {
      console.error(`Failed to fetch ${lang} translations:`, err);
      setLanguageErrors(prev => ({
        ...prev,
        [lang]: `Failed to load ${LanguageNames[lang]}`
      }));
    }
  }, [loadedTranslations, getTranslations]);

  // Load base language when it changes
  React.useEffect(() => {
    if (baseLang) {
      loadLanguage(baseLang);
    }
  }, [baseLang, loadLanguage]);

  // Load translation language when it changes
  React.useEffect(() => {
    loadLanguage(translationLang);
  }, [translationLang, loadLanguage]);

  const baseTranslationsMap = React.useMemo(() =>
    baseLang ? createTranslationMap(loadedTranslations[baseLang]?.translations) : {},
  [loadedTranslations, baseLang]
  );

  const translationMap = React.useMemo(() =>
    createTranslationMap(loadedTranslations[translationLang]?.translations),
  [loadedTranslations, translationLang]
  );

  // Count total unsaved changes across all languages
  const totalUnsavedChanges = React.useMemo(() => {
    return Object.values(editingTranslations).reduce(
      (sum, edits) => sum + Object.keys(edits).length,
      0
    );
  }, [editingTranslations]);

  // Save changes to DynamoDB for a single language
  const handleSaveLanguage = React.useCallback(async (lang: SupportedLanguage) => {
    const edits = editingTranslationsRef.current[lang];
    if (!edits || Object.keys(edits).length === 0) return;

    const editsToSave = { ...edits }; // Capture at start
    setIsSaving(true);
    try {
      await Promise.all(
        Object.entries(editsToSave).map(([key, human_translation]) =>
          updateHumanTranslation({
            lang,
            key,
            human_translation
          })
        )
      );

      setEditingTranslations(prev => {
        const updated = { ...prev };
        const remaining = { ...(updated[lang] || {}) };
        Object.keys(editsToSave).forEach(key => delete remaining[key]);
        if (Object.keys(remaining).length === 0) {
          delete updated[lang];
        } else {
          updated[lang] = remaining;
        }
        return updated;
      });

      const response = await getTranslations({ lang });
      setLoadedTranslations(prev => ({
        ...prev,
        [lang]: response as TranslationsResponse
      }));
    } catch (err) {
      console.error(`Failed to save ${lang} translations:`, err);
      alert(`Failed to save some ${LanguageNames[lang]} translations. Please try again.`);
    } finally {
      setIsSaving(false);
    }
  }, [updateHumanTranslation, getTranslations]);

  // Save all changes across all languages to DynamoDB
  const handleSaveAll = React.useCallback(async () => {
    setIsSaving(true);
    try {
      const allUpdates = Object.entries(editingTranslationsRef.current).flatMap(([lang, edits]) =>
        Object.entries(edits).map(([key, human_translation]) => ({
          lang,
          key,
          human_translation
        }))
      );
      
      const savedKeys = new Map(allUpdates.map(u => [`${u.lang}:${u.key}`, true]));

      await Promise.all(
        allUpdates.map(update => updateHumanTranslation(update))
      );

      setEditingTranslations(prev => {
        const updated = { ...prev };
        (Object.keys(updated) as SupportedLanguage[]).forEach(lang => {
          const remaining = { ...updated[lang] };
          Object.keys(remaining).forEach(key => {
            if (savedKeys.has(`${lang}:${key}`)) {
              delete remaining[key];
            }
          });
          if (Object.keys(remaining).length === 0) {
            delete updated[lang];
          } else {
            updated[lang] = remaining;
          }
        });
        return updated;
      });

      const affectedLanguages = Array.from(new Set(allUpdates.map(u => u.lang as SupportedLanguage)));
      await Promise.all(
        affectedLanguages.map(async (lang) => {
          const response = await getTranslations({ lang });
          setLoadedTranslations(prev => ({
            ...prev,
            [lang]: response as TranslationsResponse
          }));
        })
      );

      setShowSaveModal(false);
    } catch (err) {
      console.error('Failed to save all translations:', err);
      alert('Failed to save some translations. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [updateHumanTranslation, getTranslations]);

  const handleDiscardLanguage = React.useCallback((lang: SupportedLanguage) => {
    setEditingTranslations(prev => {
      const updated = { ...prev };
      delete updated[lang];
      return updated;
    });
  }, []);

  const handleDiscardAll = React.useCallback(() => {
    setEditingTranslations({});
  }, []);

  const handleRowClick = React.useCallback((key: string, trans?: Translation) => {
    setViewingKey(key);
    const localEdit = editingTranslationsRef.current[translationLang]?.[key];
    setUpdatedHumanTranslationValue(
      localEdit ?? trans?.human_translation ?? trans?.ai_translation ?? ''
    );
  }, [translationLang]);

  const handleApproveAI = React.useCallback((key: string, aiTranslation: string) => {
    setEditingTranslations(prev => ({
      ...prev,
      [translationLang]: {
        ...(prev[translationLang] || {}),
        [key]: aiTranslation
      }
    }));
  }, [translationLang]);

  const filteredKeys = React.useMemo(() => {
    let keys = Object.keys(translationMap);
    if (keys.length === 0) return [];

    if (filterBy === 'needs_review') {
      keys = keys.filter(key => translationMap[key]?.needs_review);
    } else if (filterBy === 'human_reviewed') {
      keys = keys.filter(key => translationMap[key]?.human_translation !== undefined);
    }

    if (audienceFilter !== 'all') {
      keys = keys.filter(key => translationMap[key]?.audience === audienceFilter);
    }

    if (debouncedSearchText) {
      const searchLower = debouncedSearchText.toLowerCase();
      keys = keys.filter(key => {
        const base = baseTranslationsMap[key];
        const trans = translationMap[key];
        return [
          key,
          base?.ai_translation,
          base?.human_translation,
          trans?.ai_translation,
          trans?.human_translation,
        ].some(text => text?.toLowerCase().includes(searchLower));
      });
    }

    return keys.sort((a, b) => {
      const transA = translationMap[a];
      const transB = translationMap[b];

      if (sortBy === 'last_modified') {
        const dateA = transA?.last_modified ? new Date(transA.last_modified).getTime() : 0;
        const dateB = transB?.last_modified ? new Date(transB.last_modified).getTime() : 0;
        return dateB - dateA;
      } else if (sortBy === 'needs_review') {
        if (transA?.needs_review && !transB?.needs_review) return -1;
        if (!transA?.needs_review && transB?.needs_review) return 1;
      } else if (sortBy === 'applicant_facing_first') {
        const aFacing = transA?.audience === APPLICANT_FACING;
        const bFacing = transB?.audience === APPLICANT_FACING;
        if (aFacing && !bFacing) return -1;
        if (!aFacing && bFacing) return 1;
      }
      return a.localeCompare(b);
    });
  }, [translationMap, baseTranslationsMap, debouncedSearchText, filterBy, audienceFilter, sortBy]);

  const paginatedKeys = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredKeys.slice(startIndex, endIndex);
  }, [filteredKeys, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredKeys.length / itemsPerPage);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [filterBy, audienceFilter, sortBy, debouncedSearchText, translationLang]);

  return (
    <div className="p-4">
      <TranslationControls
        baseLang={baseLang}
        setBaseLang={setBaseLang}
        translationLang={translationLang}
        setTranslationLang={setTranslationLang}
        searchText={searchText}
        setSearchText={setSearchText}
        sortBy={sortBy}
        setSortBy={setSortBy}
        filterBy={filterBy}
        setFilterBy={setFilterBy}
        audienceFilter={audienceFilter}
        setTagFilter={setTagFilter}
        baseError={baseLang ? (languageErrors[baseLang] || null) : null}
        translationError={languageErrors[translationLang] || null}
        translationStats={loadedTranslations[translationLang]?.stats || null}
      />

      {totalUnsavedChanges > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4 flex items-center justify-between">
          <div>
            <span className="font-medium">{totalUnsavedChanges}</span> unsaved change{totalUnsavedChanges !== 1 ? 's' : ''}
          </div>
          <Button
            variant="primary"
            onClick={() => setShowSaveModal(true)}
          >
            Review & Save
          </Button>
        </div>
      )}

      <TranslationsTable
        baseLang={baseLang}
        translationLang={translationLang}
        filteredKeys={paginatedKeys}
        baseTranslationsMap={baseTranslationsMap}
        translationMap={translationMap}
        editingTranslations={editingTranslations}
        translationError={languageErrors[translationLang] || null}
        onRowClick={handleRowClick}
        onApproveAI={handleApproveAI}
      />

      {/* Pagination Controls */}
      {filteredKeys.length > itemsPerPage && (
        <div className="flex items-center justify-between mt-4 px-4">
          <div className="text-sm text-gray-600">
            Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredKeys.length)} of {filteredKeys.length}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <EditTranslationModal
        viewingKey={viewingKey}
        baseLang={baseLang}
        translationLang={translationLang}
        baseTranslationsMap={baseTranslationsMap}
        translationMap={translationMap}
        updatedHumanTranslationValue={updatedHumanTranslationValue}
        onClose={() => setViewingKey(null)}
        onUpdateValue={setUpdatedHumanTranslationValue}
        onSave={() => {
          if (!viewingKey) return;

          // Save to local state
          setEditingTranslations(prev => ({
            ...prev,
            [translationLang]: {
              ...(prev[translationLang] || {}),
              [viewingKey]: updatedHumanTranslationValue
            }
          }));

          setViewingKey(null);
        }}
      />

      {showSaveModal && (
        <SaveAllModal
          editingTranslations={editingTranslations}
          allTranslationsData={loadedTranslations}
          onClose={() => setShowSaveModal(false)}
          onSaveLanguage={handleSaveLanguage}
          onSaveAll={handleSaveAll}
          onDiscardLanguage={handleDiscardLanguage}
          onDiscardAll={handleDiscardAll}
          isSaving={isSaving}
        />
      )}
    </div>
  );
};
